import type { WorkspaceService } from '.';
import type { NavigationService } from 'src/services/NavigationService';
import { UNSAVED_WORKSPACE } from 'src/store/reducers/workspace';
import { z } from 'zod';

/**
 * What an unsaved workspace starts from. Templates are references the service
 * resolves, so a spec stays small enough to ride in history state and survives
 * reloads and back/forward without carrying a stale decoded workspace.
 */
export const newWorkspaceTemplateSchema = z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('blank') }),
    z.object({ kind: z.literal('container'), workspaceId: z.string() }),
    z.object({ kind: z.literal('mosaic'), templateId: z.string() }),
]);
export type NewWorkspaceTemplate = z.infer<typeof newWorkspaceTemplateSchema>;

export const newWorkspaceSpecSchema = z.object({
    template: newWorkspaceTemplateSchema,
    name: z.string(),
    layoutSettings: z.object({
        enableMosaicLayout: z.boolean(),
        mosaicGutterPx: z.number(),
        gridSnappingEnabled: z.boolean(),
    }),
});
export type NewWorkspaceSpec = z.infer<typeof newWorkspaceSpecSchema>;

/** A blank mosaic workspace: what a plain `/dashboard/new` starts and what "new blank workspace" requests. */
export const BLANK_NEW_WORKSPACE: NewWorkspaceSpec = {
    template: { kind: 'blank' },
    name: UNSAVED_WORKSPACE.name,
    layoutSettings: {
        enableMosaicLayout: true,
        gridSnappingEnabled: UNSAVED_WORKSPACE.layoutSettings.gridSnappingEnabled,
        mosaicGutterPx: UNSAVED_WORKSPACE.layoutSettings.mosaicGutterPx ?? 0,
    },
};

const newWorkspaceRouteStateSchema = z.object({ newWorkspace: newWorkspaceSpecSchema });

/**
 * The history state of the `/dashboard/new` entry a new workspace was created at. It records how the
 * entry was reached, so Back and the dashboard link can rebuild the workspace once it has left the
 * screen; the URL stays `/dashboard/new`.
 */
export const newWorkspaceRouteState = (spec: NewWorkspaceSpec): z.infer<typeof newWorkspaceRouteStateSchema> => ({
    newWorkspace: spec,
});

/** Reads the spec a `/dashboard/new` entry's history state records; history state is untrusted, so anything else is none. */
export const parseNewWorkspaceRouteState = (state: unknown): NewWorkspaceSpec | undefined =>
    newWorkspaceRouteStateSchema.safeParse(state).data?.newWorkspace;

/**
 * Create a new unsaved workspace. Creating is an event, not a location: it replaces the workspace on
 * screen behind the unsaved-changes prompt, even one created from the same spec. On web it then
 * records where the workspace lives, so route entry finds it on screen and Back can rebuild it.
 */
export const enterNewWorkspace = async (
    deps: { readonly NavigationService?: NavigationService; readonly WorkspaceService: WorkspaceService },
    spec: NewWorkspaceSpec,
): Promise<void> => {
    if (deps.NavigationService) {
        if (!(await deps.NavigationService.confirm('replace-workspace'))) return;
        // Record the location only once the workspace is on screen: a newer navigation superseding the
        // creation, or a failure shown in place, must not pull the user back to /dashboard/new.
        if (!(await deps.WorkspaceService.createUnsaved(spec))) return;
        await deps.NavigationService.navigate('/dashboard/new', { state: newWorkspaceRouteState(spec) });
        return;
    }
    await deps.WorkspaceService.createUnsaved(spec);
};
